import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Abi, Hex } from 'viem';
import { MARKET } from '../../shared/market.generated';
import type { Address } from '../../shared/types';
import { errorsAbi, explainError } from '../lib/errors';
import { useWallet } from '../wallet/WalletProvider';
import { publicClient, useRefreshAfterTx } from './queries';

export interface Toast { id: number; kind: 'pending' | 'success' | 'error'; title: string; body?: string; hash?: Hex }
export interface TxRequest { label: string; address: Address; abi: Abi; functionName: string; args: readonly unknown[] }
export type TxPhase = 'idle' | 'simulating' | 'wallet' | 'pending';
export type TxResult = { ok: true; hash: Hex } | { ok: false; error: string };

interface TxContextValue {
  toasts: Toast[];
  dismiss(id: number): void;
  /** Shows a toast that is not tied to a transaction (auto-dismissed after `ttl` ms). */
  notify(toast: Omit<Toast, 'id'>, ttl?: number): void;
  /** Simulates, asks the wallet to sign, waits for the receipt, then refreshes market and user data. Never throws. */
  send(req: TxRequest, onPhase?: (p: TxPhase) => void): Promise<TxResult>;
}
const Ctx = createContext<TxContextValue | null>(null);

export function TxProvider({ children }: { children: ReactNode }) {
  const { client, address } = useWallet();
  const refresh = useRefreshAfterTx();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const put = useCallback((toast: Toast, ttl?: number) => {
    setToasts((ts) => [...ts.filter((x) => x.id !== toast.id), toast]);
    if (ttl) setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== toast.id)), ttl);
  }, []);
  const dismiss = useCallback((id: number) => setToasts((ts) => ts.filter((x) => x.id !== id)), []);
  const notify = useCallback<TxContextValue['notify']>((toast, ttl = 10_000) => put({ id: nextId.current++, ...toast }, ttl), [put]);

  const send = useCallback<TxContextValue['send']>(async (req, onPhase) => {
    const id = nextId.current++;
    const fail = (e: unknown): TxResult => {
      const error = explainError(e);
      put({ id, kind: 'error', title: `${req.label} failed`, body: error }, 12_000);
      onPhase?.('idle');
      return { ok: false, error };
    };
    if (!client || !address) return fail(new Error('Connect a wallet first.'));
    try {
      onPhase?.('simulating');
      // Dry-run against the public RPC first: a revert is explained here instead of surfacing as an opaque wallet error.
      const abi = [...req.abi, ...errorsAbi] as Abi;
      await publicClient.simulateContract({ account: address, address: req.address, abi, functionName: req.functionName, args: req.args as unknown[] });
      onPhase?.('wallet');
      const hash = await client.writeContract({ account: address, chain: client.chain, address: req.address, abi, functionName: req.functionName, args: req.args as unknown[] });
      onPhase?.('pending');
      put({ id, kind: 'pending', title: 'Transaction sent', body: req.label, hash });
      const receipt = await publicClient.waitForTransactionReceipt({ hash, timeout: 90_000, pollingInterval: 1_000 });
      if (receipt.status !== 'success') throw new Error('The transaction reverted on-chain.');
      await refresh(address);
      put({ id, kind: 'success', title: `${req.label} confirmed`, hash }, 8_000);
      onPhase?.('idle');
      return { ok: true, hash };
    } catch (e) {
      return fail(e);
    }
  }, [client, address, put, refresh]);

  const value = useMemo(() => ({ toasts, dismiss, notify, send }), [toasts, dismiss, notify, send]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTx(): TxContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTx outside TxProvider');
  return v;
}

export const explorerTx = (hash: string): string => `${MARKET.explorer}/tx/${hash}`;
export const explorerAddress = (a: string): string => `${MARKET.explorer}/address/${a}`;
